import React, { useEffect, useState } from "react";
import { Drawer, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Link } from "react-router-dom";

import { getBulkJobResults } from "../../../api/blankProducts";
import { getNeckLabelJobResults } from "../../../api/neckLabels";
import { getDstReplaceJobResults } from "../../../api/dstLibrary";
import { getPlacementJobResults } from "../../../api/placementDuplicate";
import { blankSettingsPathFor } from "../../../helpers/blankSettingsNav";

const STATUS_LABEL = {
  failed: "Failed",
  skipped: "Skipped",
  updated: "Updated",
  unchanged: "Already set",
};

const csvCell = (value) => {
  const s = value == null ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, jobId, neck = false) => {
  const header = ["Product", "Product ID", "Result", "Reason or change"];
  const lines = [header, ...rows.map((r) => [
    r.name || "",
    neck ? `${r.kind === "listing" ? "Listing" : "Product"} ${r.id}` : r.blankId,
    STATUS_LABEL[r.status] || r.status,
    r.reason || r.summary || "",
  ])].map((cols) => cols.map(csvCell).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${neck ? "neck-labels" : "print-methods"}-${jobId}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Skipped and failed products for one bulk job, with a CSV of every result. */
const BulkJobDetailsDrawer = ({ job, onClose }) => {
  // DST and neck-label results are per product / listing (kind + id).
  const neck =
    job?.source === "neck_labels" ||
    job?.source === "dst_replace" ||
    job?.source === "placement_duplicate";
  const fetchResults =
    job?.source === "placement_duplicate"
      ? getPlacementJobResults
      : job?.source === "dst_replace"
      ? getDstReplaceJobResults
      : neck
        ? getNeckLabelJobResults
        : getBulkJobResults;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!job?.jobId) return;
    let alive = true;
    setLoading(true);
    setError("");
    fetchResults(job.jobId, ["failed", "skipped"])
      .then((res) => alive && setRows(res?.data?.results || []))
      .catch((err) =>
        alive &&
        setError(err?.response?.data?.message || "Couldn't load the details. Try again.")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.jobId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetchResults(job.jobId);
      downloadCsv(res?.data?.results || [], job.jobId, neck);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't build the CSV. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const failed = rows.filter((r) => r.status === "failed");
  const skipped = rows.filter((r) => r.status === "skipped");

  const section = (title, list) =>
    list.length ? (
      <div className="bjd-section">
        <h3>{title}</h3>
        <ul>
          {list.map((r) => (
            <li key={neck ? `${r.kind}_${r.id}` : r.blankId}>
              {neck ? (
                <Link to={r.kind === "listing" ? `/listings/${r.id}` : "/products"} onClick={onClose}>
                  {r.name || r.id}
                </Link>
              ) : (
                <Link to={blankSettingsPathFor(r.blankId, "print-methods")} onClick={onClose}>
                  {r.name || r.blankId}
                </Link>
              )}
              <span>{r.reason || "No reason recorded"}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <Drawer anchor="right" open={Boolean(job)} onClose={onClose}>
      <div className="bjd">
        <div className="bjd-head">
          <div>
            <h2>Bulk update details</h2>
            <p>{job?.label}</p>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        {loading ? (
          <div className="bjd-empty">
            <CircularProgress size={20} />
          </div>
        ) : error ? (
          <div className="bjd-error">{error}</div>
        ) : !rows.length ? (
          <div className="bjd-empty">
            {neck ? "Everything was updated or kept its own label." : "Every product was updated or already set this way."}
          </div>
        ) : (
          <>
            {section(`Failed (${failed.length})`, failed)}
            {section(`Skipped (${skipped.length})`, skipped)}
          </>
        )}
        <div className="bjd-foot">
          <button
            type="button"
            className="bjd-btn"
            onClick={handleDownload}
            disabled={downloading || !job}
          >
            <DownloadIcon fontSize="inherit" />
            {downloading ? "Preparing…" : "Download CSV"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default BulkJobDetailsDrawer;
